import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCollectionsActionsPending,
  selectFetchCollectionsInfo,
  selectFetchInfluencerMassDmHistoryInfo,
} from '../../../store/slices/collections/slice';
import {
  cancelInfluencerManualMassDm,
  fetchCollections,
  fetchInfluencerMassDmHistory,
  sendMassDMMessage,
} from '../../../store/slices/collections/asyncThunks';
import {
  AdditionalOptionsContainer,
  CancelButton,
  CancelContainer,
  ClearMediaText,
  CloseButton,
  Content,
  ContentContainer,
  ContentHeader,
  DateDivider,
  DateDividerLine,
  Filter,
  FilterButton,
  Footer,
  FooterButton,
  FooterContent,
  FooterRow,
  FreePreviewWrapper,
  Header,
  InfiniteScrollMarker,
  ListContainer,
  LoaderContainer,
  LoaderMoreContainer,
  MediaFooter,
  MediaWrapper,
  MessageBubble,
  MessageRow,
  Navigator,
  NumberInput,
  NumOfUsersText,
  PageContainer,
  PriceInput,
  RepresentativeMedia,
  Row,
  RowContent,
  RowItem,
  SelectedMediaContainer,
  TimeText,
  Title,
  VoiceResponseText,
} from './MassDMPage.styles';
import {
  DatePicker,
  Image,
  Input,
  SelectBundleModal,
  SelectMediaModal,
  Spinner,
  UploadMediaButtonAlt,
  Video,
} from '../../../components';
import { Text14Light, Text16Regular, Text18Bold } from '../../../components/utils/Text/Text.styles';
import { formatNumber, formatPrice, objectToFormData } from '../../../utils/util';
import { ReactComponent as CheckCircleEmptyIcon } from '../../../assets/icons/check-circle-empty.svg';
import { ReactComponent as CheckCircleIcon } from '../../../assets/icons/check-circle-broken.svg';
import { ReactComponent as MicrophoneIcon } from '../../../assets/icons/microphone-01.svg';
import { ReactComponent as RefreshIcon } from '../../../assets/icons/refresh.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as SendIcon } from '../../../assets/icons/send.svg';
import { ReactComponent as PlayIcon } from '../../../assets/icons/play.svg';
import * as yup from 'yup';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ALBUM_MEDIA_TYPE,
  MANUAL_CHAT_SECRET,
  MEDIA_ASSET_SALE_TAG,
  UPLOAD_FILE_CONSTRAINTS,
} from '../../../utils/constants';
import MediaItem from '../../../components/MediaItem/MediaItem';
import UploadChatMediaButton from '../../../components/chat/UploadChatMediaButton/UploadChatMediaButton';
import { selectUserActionsPending } from '../../../store/slices/user/slice';
import { notifyError, notifyInfo } from '../../../utils/notify';
import {
  fetchBundleById,
  testVoice,
  uploadAssistedPhoto,
  uploadAssistedVideo,
} from '../../../store/slices/user/asyncThunks';
import { useParams } from 'react-router-dom';
import PreviewAlbumMediaModal from '../../../components/modals/PreviewAlbumMediaModal/PreviewAlbumMediaModal';
import { format, isAfter } from 'date-fns';
import MassDMBundle from '../../../components/chat/MassDMBundle/MassDMBundle';
import { useConfirmationModal } from '../../../components/modals/ConfirmationModal/ConfirmationModal';

const TABS = {
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
};

const sendMessageFormSchema = yup.object({
  mediaTags: yup.array().of(yup.string()).default([]),
  message: yup.string().when('mediaTags', {
    is: tags => !tags || tags.length === 0,
    then: s => s.required('Message is required.'),
    otherwise: s => s.optional(),
  }),
  secret: yup.string().required('Secret is required'),
  scheduleDate: yup.date().nullable().min(new Date(), 'Date must be in the future'),
  expireInHours: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError('Hours must be a number')
    .min(1, 'Hours must be between 1 and 47')
    .max(47, 'Hours must be between 1 and 47'),

  expireInMinutes: yup
    .number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError('Minutes must be a number')
    .min(1, 'Minutes must be between 1 and 59')
    .max(59, 'Minutes must be between 1 and 59'),
  price: yup
    .number()
    .transform((value, originalValue) => {
      return originalValue === '' ? undefined : value;
    })
    .when('mediaTags', {
      is: tags => tags?.includes('SINGLE_PAID'),
      then: n =>
        n
          .typeError('Price must be a number')
          .required('Price is required.')
          .moreThan(0, 'Price must be greater than 0'),
      otherwise: n => n.optional(),
    }),
  media: yup.array().when('mediaTags', {
    is: tags => tags?.includes('SINGLE_FREE'),
    then: arr => arr.min(1, 'You must upload exactly 1 media item').max(1, 'Only one free media item is allowed.'),
    otherwise: arr =>
      arr.when('mediaTags', {
        is: tags => tags?.includes('SINGLE_PAID'),
        then: arr => arr.min(1, 'You must upload at least 1 media item').max(10, 'You can upload up to 10 items only'),
        otherwise: arr => arr.optional(),
      }),
  }),
});

const MassDMPage = () => {
  const dispatch = useDispatch();
  const { influencerId } = useParams();

  const selectMediaModalRef = useRef();
  const selectBundleModalRef = useRef();
  const previewMediaModalRef = useRef();
  const loadMoreRef = useRef();
  const chatContainerRef = useRef(null);

  const [tab, setTab] = useState(TABS.INCLUDE);
  const [selectedInclude, setSelectedInclude] = useState([]);
  const [selectedExclude, setSelectedExclude] = useState([]);
  const [footerHeight, setFooterHeight] = useState(0);
  const [footerRef, setFooterRef] = useState(null);
  const [voiceBuffer, setVoiceBuffer] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [bundleId, setBundleId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [page, setPage] = useState(1);

  const { testVoice: testVoicePending } = useSelector(selectUserActionsPending);
  const { data, pending } = useSelector(selectFetchCollectionsInfo);
  const {
    sendMassDMMessage: sendMassDMMessagePending,
    cancelInfluencerManualMassDm: cancelInfluencerManualMassDmPending,
  } = useSelector(selectCollectionsActionsPending);
  const {
    data: messages,
    maxReached,
    pending: messagesLoading,
    fetchMorePending,
  } = useSelector(selectFetchInfluencerMassDmHistoryInfo);

  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  useEffect(() => {
    dispatch(fetchCollections({ ...(influencerId && { influencerId }) }));
  }, [dispatch, influencerId]);

  useEffect(() => {
    setPage(1);
    dispatch(fetchInfluencerMassDmHistory({ page: 1, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(() => {
        setTimeout(() => {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
          }
        }, 100);
      })
      .catch(() => {});
  }, [dispatch, influencerId]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !messagesLoading && !fetchMorePending && !maxReached) {
        const previousScrollHeight = chatContainerRef.current?.scrollHeight || 0;
        setPage(prev => prev + 1);
        dispatch(fetchInfluencerMassDmHistory({ page: page + 1, ...(influencerId && { influencerId }) }))
          .unwrap()
          .then(() => {
            if (page !== 1) {
              chatContainerRef.current.scrollTop = chatContainerRef.current?.scrollHeight - previousScrollHeight;
            }
          });
      }
    },
    [messagesLoading, maxReached, fetchMorePending, page, dispatch, influencerId],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, { root: null, threshold: 1.0 });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleIntersection]);

  useEffect(() => {
    if (!footerRef) return;

    const observer = new ResizeObserver(() => {
      setFooterHeight(footerRef.offsetHeight);
    });

    observer.observe(footerRef);

    return () => observer.disconnect();
  }, [footerRef]);

  const handleRefCallback = node => {
    if (node) {
      setFooterRef(node);
    }
  };

  const isSelected = useCallback(
    collectionId => {
      if (tab === TABS.EXCLUDE) {
        return selectedExclude.includes(collectionId);
      } else {
        return selectedInclude.includes(collectionId);
      }
    },
    [tab, selectedExclude, selectedInclude],
  );

  const onSelectItem = collectionId => {
    if (tab === TABS.EXCLUDE) {
      if (selectedExclude.includes(collectionId)) {
        setSelectedExclude(selectedExclude.filter(c => c !== collectionId));
      } else {
        if (collectionId === 'all') {
          setSelectedExclude([collectionId]);
        } else {
          setSelectedExclude([...selectedExclude.filter(c => c !== 'all'), collectionId]);
        }
      }
    } else {
      if (selectedInclude.includes(collectionId)) {
        setSelectedInclude(selectedInclude.filter(c => c !== collectionId));
      } else {
        if (collectionId === 'all') {
          setSelectedInclude([collectionId]);
        } else {
          setSelectedInclude([...selectedInclude.filter(c => c !== 'all'), collectionId]);
        }
      }
    }
  };

  const arrayList = useMemo(() => {
    if (tab === TABS.INCLUDE) {
      return [{ _id: 'all', name: 'All Fans' }, ...data];
    } else {
      return data.filter(obj => !selectedInclude.includes(obj._id));
    }
  }, [data, tab, selectedInclude]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm({
    delayError: 300,
    defaultValues: {
      message: '',
      price: '',
      freePreviewMedia: null,
      scheduleDate: null,
      media: [],
      mediaTags: [],
      expireInHours: '',
      expireInMinutes: '',
      secret: '',
    },
    resolver: yupResolver(sendMessageFormSchema),
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'media' });

  const freePreviewMediaWatch = watch('freePreviewMedia');
  const mediaTags = watch('mediaTags');

  useEffect(() => {
    setConfirmationModalLoader(cancelInfluencerManualMassDmPending);
  }, [cancelInfluencerManualMassDmPending, setConfirmationModalLoader]);

  const onTestVoice = () => {
    const message = watch('message');
    if (!message.trim()) {
      notifyError('Please enter text');
      return;
    }
    setVoiceBuffer(null);
    dispatch(testVoice({ message, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(({ speech }) => {
        setVoiceBuffer(speech.data);
        const audioBlob = new Blob([new Uint8Array(speech.data)], { type: 'audio/mp3' });
        const receivedVoiceUrl = URL.createObjectURL(audioBlob);
        const audio = new Audio(receivedVoiceUrl);
        audio.play();
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const onPlayVoice = () => {
    const audioBlob = new Blob([new Uint8Array(voiceBuffer)], { type: 'audio/mp3' });
    const receivedVoiceUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(receivedVoiceUrl);
    audio.play();
  };

  const onSelectMedia = media => {
    setBundleId(null);
    remove();
    if (Array.isArray(media)) {
      if (media.length === 1) {
        if (media[0].caption) {
          setValue('message', media[0].caption);
        }
        if (media[0].cost) {
          setValue('price', media[0].cost);
        }
      }
      for (const m of media) {
        append(m);
      }
    } else {
      append(media);
      if (media.caption) {
        setValue('message', media.caption);
      }
      if (media.cost) {
        setValue('price', media.cost);
      }
    }
  };

  const onSelectBundle = bundle => {
    setBundleId(null);
    remove();
    dispatch(fetchBundleById({ bundleId: bundle.id, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(({ bundle }) => {
        setBundleId(bundle.id);
        if (bundle.representativeMedia) {
          setValue('freePreviewMedia', {
            file: { type: bundle.representativeMedia.type, path: bundle.representativeMedia.path },
            url: bundle.representativeMedia.url,
          });
        }
        if (bundle.caption) {
          setValue('message', bundle.caption);
        }
        if (bundle.cost) {
          setValue('price', bundle.cost);
        }
        for (const m of bundle.media) {
          append(m);
        }
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const onProgress = progress => {
    setProgress(progress > 99 ? 99 : progress.toFixed(1));
  };

  const sendChatMessage = async data => {
    const body = {};
    if (data.secret !== MANUAL_CHAT_SECRET) {
      notifyError('Wrong secret!');
      return;
    }

    if (!data.mediaTags.length) {
      body.text = data.message;
      if (voiceBuffer) {
        body.voiceBuffer = new Uint8Array(voiceBuffer).buffer;
      }
    } else {
      if (data.message) {
        body.text = data.message;
      }
      if (data.mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID)) {
        const freePreviewMedia = data.freePreviewMedia;

        let representativeFilename = null;
        let representativeType = null;
        if (freePreviewMedia) {
          if (!freePreviewMedia.url.startsWith('https://')) {
            const freePreviewType = freePreviewMedia.file.type.includes('video')
              ? ALBUM_MEDIA_TYPE.VIDEO
              : ALBUM_MEDIA_TYPE.PHOTO;
            representativeType = freePreviewType;
            if (freePreviewType === ALBUM_MEDIA_TYPE.PHOTO) {
              const response = await dispatch(
                uploadAssistedPhoto({
                  photo: freePreviewMedia,
                  type: MEDIA_ASSET_SALE_TAG.BUNDLE,
                  representativeMedia: true,
                  onProgress,
                  influencerId,
                }),
              ).unwrap();

              representativeFilename = response.fileName;
            } else {
              const response = await dispatch(
                uploadAssistedVideo({
                  video: freePreviewMedia,
                  type: MEDIA_ASSET_SALE_TAG.BUNDLE,
                  representativeMedia: true,
                  onProgress,
                  influencerId,
                }),
              ).unwrap();
              representativeFilename = response.fileName;
            }
            body.representativeMediaType = representativeType;
            body.representativeMediaPath = representativeFilename;
          } else {
            body.representativeMediaType = freePreviewMedia.file.type;
            body.representativeMediaPath = freePreviewMedia.file.path;
          }
        }
        body.cost = data.price;
      }

      const albumMediaIds = data.media.map(m => m.id);
      if (bundleId) {
        body.albumMediaIds = [bundleId];
      } else {
        body.albumMediaIds = albumMediaIds;
      }
    }

    if (data.scheduleDate) {
      body.scheduleDate = data.scheduleDate;
    }
    if (data.expireInMinutes || data.expireInHours) {
      let expireInMinutes = 0;
      if (data.expireInMinutes) {
        expireInMinutes += data.expireInMinutes;
      }
      if (data.expireInHours) {
        expireInMinutes += data.expireInHours * 60;
      }

      body.expireInMinutes = expireInMinutes;
    }

    body.includedCollectionIds = selectedInclude.includes('all') ? [] : selectedInclude;
    body.excludedCollectionIds = selectedExclude;

    const formData = objectToFormData(body);

    dispatch(sendMassDMMessage({ body: formData, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(() => {
        reset();
        setSelectedInclude([]);
        setSelectedExclude([]);
        setVoiceBuffer(null);
        notifyInfo('Mass DM started successfully');
        setPage(1);
        dispatch(fetchInfluencerMassDmHistory({ page: 1, ...(influencerId && { influencerId }) }))
          .unwrap()
          .then(() => {
            setTimeout(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
              }
            }, 100);
          })
          .catch(() => {});
      })
      .catch(err => {
        setProgress(0);
        notifyError(err.message);
      });
  };

  const cancelMassDm = id => {
    openModal(
      'Cancel Mass DM',
      `Are you sure? This action will cancel the mass dm.`,
      'Cancel',
      () => {
        dispatch(cancelInfluencerManualMassDm({ massDmId: id, ...(influencerId && { influencerId }) }))
          .unwrap()
          .then(() => {
            notifyInfo('Mass DM canceled successfully');
            closeModal();
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  return (
    <>
      <PageContainer>
        <Navigator>
          <Header>
            <Title>New Message</Title>
            <Filter>
              <FilterButton onClick={() => setTab(TABS.INCLUDE)} $selected={tab === TABS.INCLUDE}>
                Include
              </FilterButton>
              <FilterButton onClick={() => setTab(TABS.EXCLUDE)} $selected={tab === TABS.EXCLUDE}>
                Exclude
              </FilterButton>
            </Filter>
          </Header>
          {pending ? (
            <LoaderContainer>
              <Spinner width={120} height={120} />
            </LoaderContainer>
          ) : (
            <ListContainer>
              {arrayList.map(collection => {
                return (
                  <RowItem key={collection._id} onClick={() => onSelectItem(collection._id)}>
                    {isSelected(collection._id) ? (
                      <CheckCircleIcon color="#1672BA" width={24} height={24} />
                    ) : (
                      <CheckCircleEmptyIcon color="#1672BA" width={24} height={24} />
                    )}
                    <RowContent>
                      <Text16Regular>{collection.name}</Text16Regular>
                      <NumOfUsersText>
                        {collection.totalUsers !== undefined ? `${formatNumber(collection.totalUsers)} users` : ''}
                      </NumOfUsersText>
                    </RowContent>
                  </RowItem>
                );
              })}
            </ListContainer>
          )}
        </Navigator>
        <Content>
          <ContentHeader>
            <Text18Bold>Select users to send them a message</Text18Bold>
          </ContentHeader>
          <ContentContainer ref={chatContainerRef} $footerHeight={footerHeight}>
            {messagesLoading ? (
              <LoaderContainer>
                <Spinner height={100} width={100} />
              </LoaderContainer>
            ) : (
              <>
                {!!messages.length && <InfiniteScrollMarker ref={loadMoreRef} />}
                <LoaderMoreContainer>{fetchMorePending && <Spinner width={50} height={50} />}</LoaderMoreContainer>
                {messages.map((item, index) => {
                  const currentDate = new Date(item.createdAt).toDateString();
                  const previousDate = index > 0 ? new Date(messages[index - 1].createdAt).toDateString() : null;
                  const isNewDay = currentDate !== previousDate;
                  return (
                    <MessageRow key={item._id} $role={'INFLUENCER'}>
                      {isNewDay && (
                        <DateDivider>
                          <DateDividerLine />
                          <Text14Light>{format(currentDate, 'MMM d, yyyy')}</Text14Light>
                          <DateDividerLine />
                        </DateDivider>
                      )}
                      {item.bundle && <MassDMBundle item={item} onCancel={cancelMassDm} />}
                      {!!item.media.length ? (
                        <MediaWrapper>
                          {item.representativeMedia && (
                            <RepresentativeMedia>
                              <FreePreviewWrapper>FREE PREVIEW</FreePreviewWrapper>
                              {item.representativeMedia.type === ALBUM_MEDIA_TYPE.PHOTO ? (
                                <Image src={item.representativeMedia.url} />
                              ) : (
                                <Video src={item.representativeMedia.url} />
                              )}
                            </RepresentativeMedia>
                          )}
                          {item.media.map(m => {
                            return (
                              <Image
                                key={m.id}
                                src={m.thumbnailUrl}
                                onClick={() => {
                                  setSelectedMedia({ url: m.url, kind: m.kind });
                                  previewMediaModalRef.current.show();
                                }}
                              />
                            );
                          })}
                          <MediaFooter $role={'INFLUENCER'}>
                            <FooterRow>
                              <TimeText $role={'INFLUENCER'}>
                                Cost: {item.cost ? `$${formatPrice(item.cost)}` : 'Free'}
                              </TimeText>
                            </FooterRow>
                            {item.scheduleDate && (
                              <FooterRow>
                                <TimeText $role={'INFLUENCER'}>
                                  Scheduled: {format(item.scheduleDate, 'MMM d, yyyy HH:mm')}
                                </TimeText>
                              </FooterRow>
                            )}
                            {item.cancelledAt && (
                              <FooterRow>
                                <TimeText $role={'INFLUENCER'}>
                                  Canceled: {format(item.cancelledAt, 'MMM d, yyyy HH:mm')}
                                </TimeText>
                              </FooterRow>
                            )}
                            {item.expireInMinutes && (
                              <FooterRow>
                                <TimeText $role={'INFLUENCER'}>Expire In: {item.expireInMinutes}min</TimeText>
                              </FooterRow>
                            )}
                            <FooterRow>
                              <TimeText $role={'INFLUENCER'}> {item.text}</TimeText>
                            </FooterRow>
                            {item.scheduleDate && isAfter(item.scheduleDate, new Date()) && !item.cancelledAt && (
                              <FooterRow>
                                <CancelContainer>
                                  <CancelButton
                                    $disabled={cancelInfluencerManualMassDmPending}
                                    onClick={() => cancelMassDm(item._id)}>
                                    Cancel
                                  </CancelButton>
                                </CancelContainer>
                              </FooterRow>
                            )}
                            <FooterRow>
                              <TimeText $role={'INFLUENCER'} />
                              <TimeText $role={'INFLUENCER'}>{format(item.createdAt, 'HH:mm')}</TimeText>
                            </FooterRow>
                          </MediaFooter>
                        </MediaWrapper>
                      ) : (
                        !item.bundle && (
                          <MessageBubble $isAudio={!!item.voicePath}>
                            {item.text}
                            <MediaFooter $role={'INFLUENCER'}>
                              {item.scheduleDate && (
                                <FooterRow>
                                  <TimeText $role={'INFLUENCER'}>
                                    Scheduled: {format(item.scheduleDate, 'MMM d, yyyy HH:mm')}
                                  </TimeText>
                                </FooterRow>
                              )}
                              {item.cancelledAt && (
                                <FooterRow>
                                  <TimeText $role={'INFLUENCER'}>
                                    Canceled: {format(item.cancelledAt, 'MMM d, yyyy HH:mm')}
                                  </TimeText>
                                </FooterRow>
                              )}
                              {item.expireInMinutes && (
                                <FooterRow>
                                  <TimeText $role={'INFLUENCER'}>Expire In: {item.expireInMinutes}min</TimeText>
                                </FooterRow>
                              )}
                              {item.scheduleDate && isAfter(item.scheduleDate, new Date()) && !item.cancelledAt && (
                                <FooterRow>
                                  <CancelContainer>
                                    <CancelButton
                                      $disabled={cancelInfluencerManualMassDmPending}
                                      onClick={() => cancelMassDm(item._id)}>
                                      Cancel
                                    </CancelButton>
                                  </CancelContainer>
                                </FooterRow>
                              )}
                              <FooterRow>
                                <VoiceResponseText $role={'INFLUENCER'}>
                                  {!!item.voicePath ? `<audio>` : ''}
                                </VoiceResponseText>
                                <TimeText $role={'INFLUENCER'}>{format(item.createdAt, 'HH:mm')}</TimeText>
                              </FooterRow>
                            </MediaFooter>
                          </MessageBubble>
                        )
                      )}
                    </MessageRow>
                  );
                })}
              </>
            )}
          </ContentContainer>
          <Footer ref={handleRefCallback} autoComplete="off" onSubmit={handleSubmit(sendChatMessage)}>
            {!!fields.length && (
              <SelectedMediaContainer>
                {!mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_FREE) &&
                  (freePreviewMediaWatch ? (
                    <MediaItem
                      type={
                        freePreviewMediaWatch.file.type.toLowerCase().includes('video')
                          ? ALBUM_MEDIA_TYPE.VIDEO
                          : ALBUM_MEDIA_TYPE.PHOTO
                      }
                      file={freePreviewMediaWatch}
                      progress={progress}
                      onDelete={() => {
                        setValue('freePreviewMedia', null);
                        setUploadErrors(prev =>
                          prev.filter(error => error.fileName !== freePreviewMediaWatch.file.name),
                        );
                      }}
                      uploadError={uploadErrors.find(error => error.fileName === freePreviewMediaWatch.file.name)}
                    />
                  ) : (
                    <Controller
                      name="freePreviewMedia"
                      control={control}
                      render={({ field }) => {
                        return (
                          <UploadMediaButtonAlt
                            type={UPLOAD_FILE_CONSTRAINTS.TYPE.MIXED}
                            onChange={files => field.onChange(files[0])}
                            setError={setUploadErrors}
                            multiple={false}
                            title="Add Free Preview"
                          />
                        );
                      }}
                    />
                  ))}
                {fields.map((field, index) => {
                  return (
                    <MediaItem
                      key={field.id}
                      kind={field.kind}
                      type={field.kind}
                      onClick={() => {
                        setSelectedMedia(field);
                        previewMediaModalRef.current.show();
                      }}
                      file={field}
                      onDelete={
                        bundleId
                          ? null
                          : e => {
                              if (fields.length === 1) {
                                setValue('mediaTags', []);
                                setValue('price', '');
                                setValue('message', '');
                              }
                              remove(index);
                              e.stopPropagation();
                            }
                      }
                    />
                  );
                })}
              </SelectedMediaContainer>
            )}
            {fields.length > 1 && (
              <ClearMediaText
                onClick={() => {
                  remove();
                  setValue('mediaTags', []);
                  setBundleId(null);
                }}>
                Clear all
              </ClearMediaText>
            )}
            <FooterContent>
              <UploadChatMediaButton
                onClick={tags => {
                  if (Array.isArray(tags)) {
                    setValue('mediaTags', tags);
                    selectMediaModalRef.current.show();
                  } else {
                    setValue('mediaTags', [MEDIA_ASSET_SALE_TAG.SINGLE_PAID]);
                    selectBundleModalRef.current.show();
                  }
                }}
              />
              <Input
                placeholder="Type a Message"
                {...register('message')}
                error={
                  errors.message?.message ||
                  errors.price?.message ||
                  errors.media?.message ||
                  errors.media?.root?.message
                }
              />
              {mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID) && !!fields.length && (
                <PriceInput leftIcon="$" placeholder="Price" {...register('price')} />
              )}
              {!!voiceBuffer && (
                <FooterButton type="button" disabled={testVoicePending} onClick={onPlayVoice}>
                  <PlayIcon />
                </FooterButton>
              )}
              {!fields.length && (
                <FooterButton type="button" disabled={testVoicePending} onClick={onTestVoice} $active={!!voiceBuffer}>
                  {voiceBuffer ? (
                    <RefreshIcon color={!!voiceBuffer ? '#fff' : '#1672BA'} />
                  ) : (
                    <MicrophoneIcon color={!!voiceBuffer ? '#fff' : '#1672BA'} />
                  )}
                  {!!voiceBuffer && (
                    <CloseButton
                      onClick={e => {
                        setVoiceBuffer(null);
                        e.stopPropagation();
                      }}>
                      <CloseIcon color="#fff" width={12} height={12} />
                    </CloseButton>
                  )}
                </FooterButton>
              )}
              <FooterButton disabled={sendMassDMMessagePending}>
                <SendIcon />
              </FooterButton>
            </FooterContent>
            <AdditionalOptionsContainer>
              <Row>
                <Controller
                  name="scheduleDate"
                  control={control}
                  render={({ field }) => {
                    return (
                      <DatePicker
                        label="Schedule Date"
                        placeholder="Enter schedule date"
                        value={field.value}
                        onChange={field.onChange}
                        showTimeSelect
                        error={errors.scheduleDate?.message}
                      />
                    );
                  }}
                />
                <Input
                  label="Secret"
                  placeholder="Enter Secret"
                  {...register('secret')}
                  error={errors.secret?.message}
                />
              </Row>
              <Row>
                <NumberInput
                  label="Expire In (HH:mm)"
                  placeholder="HH"
                  error={errors?.expireInHours?.message}
                  {...register(`expireInHours`)}
                />
                <NumberInput
                  label="&nbsp;"
                  placeholder="mm"
                  error={errors?.expireInMinutes?.message}
                  {...register(`expireInMinutes`)}
                />
              </Row>
            </AdditionalOptionsContainer>
          </Footer>
        </Content>
        <SelectMediaModal
          modalRef={selectMediaModalRef}
          tag={mediaTags}
          onAdd={onSelectMedia}
          influencerId={influencerId}
          maxSelectedItems={10}
          exactMatch={!mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_PAID)}
          multiple={!mediaTags.includes(MEDIA_ASSET_SALE_TAG.SINGLE_FREE)}
        />
        <SelectBundleModal modalRef={selectBundleModalRef} onAdd={onSelectBundle} influencerId={influencerId} />
      </PageContainer>
      <PreviewAlbumMediaModal
        modalRef={previewMediaModalRef}
        url={selectedMedia?.url}
        type={selectedMedia?.kind}
        setSelectedUrl={setSelectedMedia}
      />
    </>
  );
};

export default MassDMPage;
